import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { AppLayout } from '../../components/layout/AppLayout';
import { useVehicleStore } from '../../stores/vehicleStore';
import theme from '../../theme';

interface ExportProgress {
  phase: 'idle' | 'preparing' | 'fetching' | 'processing' | 'generating' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
}

interface ExportStats {
  totalRecords: number;
  exportedRecords: number;
  fileSize: string;
  format: string;
  fieldsIncluded: number;
  totalFields: number;
  vehicleBreakdown: {
    available: number;
    rented: number;
    maintenance: number;
    inactive: number;
  };
}

interface ExportField {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  required?: boolean;
}

export const VehicleExportScreen = () => {
  const navigation = useNavigation();
  const { vehicles, fetchVehicles } = useVehicleStore();
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'json' | 'excel'>('csv');
  const [progress, setProgress] = useState<ExportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 5,
    message: 'Ready to export Vehicle database',
  });
  const [exportStats, setExportStats] = useState<ExportStats | null>(null);

  const [exportFields, setExportFields] = useState<ExportField[]>([
    { key: 'id', label: 'Vehicle ID', description: 'Unique vehicle identifier', enabled: true, required: true },
    { key: 'plate_number', label: 'Plate Number', description: 'Vehicle license plate', enabled: true, required: true },
    { key: 'make', label: 'Make', description: 'Vehicle manufacturer', enabled: true },
    { key: 'model', label: 'Model', description: 'Vehicle model', enabled: true },
    { key: 'year', label: 'Year', description: 'Manufacturing year', enabled: true },
    { key: 'color', label: 'Color', description: 'Vehicle color', enabled: true },
    { key: 'vin', label: 'VIN Number', description: 'Vehicle identification number', enabled: false },
    { key: 'status', label: 'Status', description: 'Current vehicle status', enabled: true },
    { key: 'current_driver_id', label: 'Current Driver', description: 'Assigned driver ID', enabled: true },
    { key: 'rental_rate', label: 'Company Share', description: 'Daily company share amount', enabled: false },
    { key: 'insurance_expiry', label: 'Insurance Expiry', description: 'Insurance expiration date', enabled: false },
    { key: 'registration_expiry', label: 'Registration Expiry', description: 'Registration expiration date', enabled: false },
    { key: 'mileage', label: 'Mileage', description: 'Current vehicle mileage', enabled: false },
    { key: 'last_service', label: 'Last Service', description: 'Last maintenance date', enabled: false },
    { key: 'created_at', label: 'Created Date', description: 'Record creation date', enabled: false },
    { key: 'updated_at', label: 'Last Updated', description: 'Last modification date', enabled: false },
  ]);

  const exportFormats = [
    { id: 'csv', name: 'CSV', description: 'Comma-separated values', icon: 'document-text', color: '#059669' },
    { id: 'json', name: 'JSON', description: 'JavaScript Object Notation', icon: 'code-slash', color: '#DC2626' },
    { id: 'excel', name: 'Excel', description: 'Microsoft Excel format', icon: 'grid', color: '#16A34A' },
  ];

  const steps = [
    { id: 1, name: 'Format Selection', description: 'Choose export format and configure fields' },
    { id: 2, name: 'Data Fetching', description: 'Retrieve vehicle records from database' },
    { id: 3, name: 'Data Processing', description: 'Format and filter selected data fields' },
    { id: 4, name: 'File Generation', description: 'Generate export file in selected format' },
    { id: 5, name: 'Download Ready', description: 'File ready for download or sharing' },
  ];

  useEffect(() => {
    fetchVehicles();
  }, []);

  const toggleField = (fieldKey: string) => {
    setExportFields(prev => 
      prev.map(field => 
        field.key === fieldKey 
          ? { ...field, enabled: field.required ? true : !field.enabled }
          : field
      )
    );
  };

  const startExport = async () => {
    const enabledFields = exportFields.filter(f => f.enabled);
    if (enabledFields.length === 0) {
      Alert.alert('Error', 'Please select at least one field to export');
      return;
    }

    setProgress({ phase: 'preparing', currentStep: 1, totalSteps: 5, message: 'Preparing export...' });
    await new Promise(resolve => setTimeout(resolve, 800));

    setProgress({ 
      phase: 'fetching', 
      currentStep: 2, 
      totalSteps: 5, 
      message: 'Fetching vehicle records...',
      details: 'Connecting to database and retrieving vehicle data'
    });
    await new Promise(resolve => setTimeout(resolve, 1200));

    setProgress({ 
      phase: 'processing', 
      currentStep: 3, 
      totalSteps: 5, 
      message: 'Processing vehicle data...',
      details: 'Filtering and formatting selected fields'
    });
    
    // Process real vehicle data
    const totalVehicles = vehicles.length;
    const vehicleBreakdown = {
      available: vehicles.filter(v => v.carStatus === 'available').length,
      rented: vehicles.filter(v => v.carStatus === 'work').length,
      maintenance: vehicles.filter(v => v.carStatus === 'in_workshop').length,
      inactive: vehicles.filter(v => v.carStatus === 'stop' || v.carStatus === 'out_of_service').length,
    };
    
    for (let i = 0; i <= 100; i += 20) {
      setProgress({ 
        phase: 'processing', 
        currentStep: 3, 
        totalSteps: 5, 
        message: 'Processing vehicle data...',
        details: `Processed ${i}% of records`,
        processed: Math.floor((i / 100) * totalVehicles),
        total: totalVehicles
      });
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    setProgress({ 
      phase: 'generating', 
      currentStep: 4, 
      totalSteps: 5, 
      message: `Generating ${selectedFormat.toUpperCase()} file...`,
      details: 'Creating downloadable file with selected data'
    });
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Set final results with real data
    const estimatedSize = Math.round((totalVehicles * enabledFields.length * 15) / 1024); // Rough estimate
    const stats: ExportStats = {
      totalRecords: totalVehicles,
      exportedRecords: totalVehicles,
      fileSize: selectedFormat === 'json' ? `${Math.round(estimatedSize * 1.8)} KB` : 
                selectedFormat === 'excel' ? `${Math.round(estimatedSize * 1.4)} KB` : 
                `${estimatedSize} KB`,
      format: selectedFormat.toUpperCase(),
      fieldsIncluded: enabledFields.length,
      totalFields: exportFields.length,
      vehicleBreakdown,
    };

    setExportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 5, 
      totalSteps: 5, 
      message: 'Export completed successfully!',
      details: `${stats.exportedRecords} vehicle records exported to ${stats.format} format`
    });
  };

  const downloadFile = async () => {
    try {
      if (!exportStats) return;
      
      const enabledFields = exportFields.filter(f => f.enabled);
      let fileContent = '';
      let fileName = `vehicle_export_${new Date().toISOString().split('T')[0]}`;
      
      if (selectedFormat === 'csv') {
        const headers = enabledFields.map(f => f.label).join(',');
        const rows = vehicles.map(vehicle => 
          enabledFields.map(field => {
            const value = vehicle[field.key as keyof typeof vehicle] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        );
        fileContent = [headers, ...rows].join('\n');
        fileName += '.csv';
      } else if (selectedFormat === 'json') {
        const data = vehicles.map(vehicle => {
          const filtered: any = {};
          enabledFields.forEach(field => {
            filtered[field.key] = vehicle[field.key as keyof typeof vehicle];
          });
          return filtered;
        });
        fileContent = JSON.stringify(data, null, 2);
        fileName += '.json';
      }
      
      const fileUri = FileSystem.documentDirectory + fileName;
      await FileSystem.writeAsStringAsync(fileUri, fileContent);
      await Sharing.shareAsync(fileUri);
      
      Alert.alert('Success', `${exportStats.format} file downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      Alert.alert('Error', 'Failed to download file');
    }
  };

  const shareFile = async () => {
    try {
      if (!exportStats) return;
      
      const enabledFields = exportFields.filter(f => f.enabled);
      let fileContent = '';
      let fileName = `vehicle_export_${new Date().toISOString().split('T')[0]}`;
      
      if (selectedFormat === 'csv') {
        const headers = enabledFields.map(f => f.label).join(',');
        const rows = vehicles.map(vehicle => 
          enabledFields.map(field => {
            const value = vehicle[field.key as keyof typeof vehicle] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        );
        fileContent = [headers, ...rows].join('\n');
        fileName += '.csv';
      } else if (selectedFormat === 'json') {
        const data = vehicles.map(vehicle => {
          const filtered: any = {};
          enabledFields.forEach(field => {
            filtered[field.key] = vehicle[field.key as keyof typeof vehicle];
          });
          return filtered;
        });
        fileContent = JSON.stringify(data, null, 2);
        fileName += '.json';
      }
      
      const fileUri = FileSystem.documentDirectory + fileName;
      await FileSystem.writeAsStringAsync(fileUri, fileContent);
      await Sharing.shareAsync(fileUri);
    } catch (error) {
      console.error('Share error:', error);
      Alert.alert('Error', 'Failed to share file');
    }
  };

  const resetExport = () => {
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 5,
      message: 'Ready to export Vehicle database',
    });
    setExportStats(null);
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#7C3AED'; // Vehicle export purple
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#7C3AED" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Vehicle Export" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#7C3AED' + '15' }]}>
              <Ionicons name="car-sport" size={24} color="#7C3AED" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Vehicle Database Export</Text>
              <Text style={styles.subtitle}>Export vehicle fleet data with customizable fields and formats</Text>
            </View>
          </View>

          {/* Format Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Export Format</Text>
            <View style={styles.formatGrid}>
              {exportFormats.map((format) => (
                <TouchableOpacity
                  key={format.id}
                  style={[
                    styles.formatCard,
                    selectedFormat === format.id && { 
                      borderColor: format.color,
                      backgroundColor: format.color + '10'
                    }
                  ]}
                  onPress={() => setSelectedFormat(format.id as any)}
                >
                  <View style={[styles.formatIcon, { backgroundColor: format.color + '15' }]}>
                    <Ionicons name={format.icon as any} size={20} color={format.color} />
                  </View>
                  <Text style={[styles.formatName, selectedFormat === format.id && { color: format.color }]}>
                    {format.name}
                  </Text>
                  <Text style={styles.formatDescription}>{format.description}</Text>
                  {selectedFormat === format.id && (
                    <View style={[styles.selectedIndicator, { backgroundColor: format.color }]}>
                      <Ionicons name="checkmark" size={12} color={theme.colors.white} />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Field Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Field Selection</Text>
            <Text style={styles.sectionSubtitle}>
              Choose which vehicle information to include in the export
            </Text>
            <View style={styles.fieldsContainer}>
              {exportFields.map((field) => (
                <TouchableOpacity
                  key={field.key}
                  style={[
                    styles.fieldItem,
                    field.enabled && styles.fieldItemEnabled,
                    field.required && styles.fieldItemRequired
                  ]}
                  onPress={() => toggleField(field.key)}
                  disabled={field.required}
                >
                  <View style={styles.fieldInfo}>
                    <Text style={[
                      styles.fieldLabel,
                      field.enabled && styles.fieldLabelEnabled,
                      field.required && styles.fieldLabelRequired
                    ]}>
                      {field.label}
                      {field.required && <Text style={styles.requiredMark}> *</Text>}
                    </Text>
                    <Text style={styles.fieldDescription}>{field.description}</Text>
                  </View>
                  <Switch
                    value={field.enabled}
                    onValueChange={() => toggleField(field.key)}
                    disabled={field.required}
                    trackColor={{ false: theme.colors.gray300, true: '#7C3AED' + '30' }}
                    thumbColor={field.enabled ? '#7C3AED' : theme.colors.gray400}
                  />
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.fieldSummary}>
              <Text style={styles.fieldSummaryText}>
                {exportFields.filter(f => f.enabled).length} of {exportFields.length} fields selected
              </Text>
            </View>
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>3. Export Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#7C3AED' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {(progress.phase === 'preparing' || progress.phase === 'fetching' || 
                    progress.phase === 'processing' || progress.phase === 'generating') && (
                    <ActivityIndicator size="small" color="#7C3AED" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { 
                          width: `${(progress.processed / progress.total) * 100}%`,
                          backgroundColor: '#7C3AED'
                        }
                      ]} 
                    />
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Export Statistics */}
          {exportStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>4. Export Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{exportStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Vehicles</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#7C3AED' }]}>
                    {exportStats.exportedRecords}
                  </Text>
                  <Text style={styles.statLabel}>Exported</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.primary }]}>
                    {exportStats.fieldsIncluded}
                  </Text>
                  <Text style={styles.statLabel}>Fields</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.success }]}>
                    {exportStats.fileSize}
                  </Text>
                  <Text style={styles.statLabel}>File Size</Text>
                </View>
              </View>

              {/* Vehicle Status Breakdown */}
              <View style={styles.vehicleSection}>
                <Text style={styles.vehicleTitle}>Vehicle Status</Text>
                <View style={styles.vehicleGrid}>
                  <View style={styles.vehicleCard}>
                    <Ionicons name="checkmark-circle" size={20} color="#10B981" />
                    <Text style={styles.vehicleNumber}>{exportStats.vehicleBreakdown.available}</Text>
                    <Text style={styles.vehicleLabel}>Available</Text>
                  </View>
                  <View style={styles.vehicleCard}>
                    <Ionicons name="car" size={20} color="#3B82F6" />
                    <Text style={styles.vehicleNumber}>{exportStats.vehicleBreakdown.rented}</Text>
                    <Text style={styles.vehicleLabel}>In Use</Text>
                  </View>
                  <View style={styles.vehicleCard}>
                    <Ionicons name="construct" size={20} color="#F59E0B" />
                    <Text style={styles.vehicleNumber}>{exportStats.vehicleBreakdown.maintenance}</Text>
                    <Text style={styles.vehicleLabel}>Maintenance</Text>
                  </View>
                  <View style={styles.vehicleCard}>
                    <Ionicons name="pause-circle" size={20} color="#9CA3AF" />
                    <Text style={styles.vehicleNumber}>{exportStats.vehicleBreakdown.inactive}</Text>
                    <Text style={styles.vehicleLabel}>Inactive</Text>
                  </View>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Export Format:</Text>
                  <Text style={styles.summaryValue}>
                    {exportStats.format}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Vehicles Exported:</Text>
                  <Text style={[styles.summaryValue, { color: '#7C3AED' }]}>
                    {exportStats.exportedRecords}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Fields Included:</Text>
                  <Text style={styles.summaryValue}>
                    {exportStats.fieldsIncluded} / {exportStats.totalFields}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Export Date:</Text>
                  <Text style={styles.summaryValue}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
              </View>

              {/* Export-specific insights */}
              <View style={styles.insightCard}>
                <View style={styles.insightHeader}>
                  <Ionicons name="analytics" size={20} color="#7C3AED" />
                  <Text style={styles.insightTitle}>Vehicle Export Analysis</Text>
                </View>
                <Text style={styles.insightText}>
                  • Complete vehicle fleet exported with {exportStats.fieldsIncluded} data fields
                </Text>
                <Text style={styles.insightText}>
                  • {exportStats.vehicleBreakdown.rented} vehicles currently in use ({((exportStats.vehicleBreakdown.rented / exportStats.totalRecords) * 100).toFixed(0)}% utilization)
                </Text>
                <Text style={styles.insightText}>
                  • Export file size: {exportStats.fileSize} in {exportStats.format} format
                </Text>
                <Text style={styles.insightText}>
                  • Ready for fleet management and analysis systems
                </Text>
              </View>

              {/* Download Actions */}
              <View style={styles.downloadSection}>
                <Text style={styles.downloadTitle}>Download Options</Text>
                <View style={styles.downloadButtons}>
                  <TouchableOpacity 
                    style={[styles.downloadButton, { backgroundColor: '#7C3AED' }]} 
                    onPress={downloadFile}
                  >
                    <Ionicons name="download" size={20} color={theme.colors.white} />
                    <Text style={styles.downloadButtonText}>Download File</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={[styles.downloadButton, { backgroundColor: theme.colors.primary }]} 
                    onPress={shareFile}
                  >
                    <Ionicons name="share" size={20} color={theme.colors.white} />
                    <Text style={styles.downloadButtonText}>Share File</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {progress.phase === 'idle' || progress.phase === 'complete' ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#7C3AED' },
                    exportFields.filter(f => f.enabled).length === 0 && styles.disabledButton
                  ]} 
                  onPress={startExport}
                  disabled={exportFields.filter(f => f.enabled).length === 0 || (progress.phase !== 'idle' && progress.phase !== 'complete')}
                >
                  <Ionicons name="download" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    {progress.phase === 'complete' ? 'Export Again' : 'Start Export'}
                  </Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#7C3AED' }]} 
                    onPress={resetExport}
                  >
                    <Ionicons name="refresh" size={20} color="#7C3AED" />
                    <Text style={[styles.secondaryButtonText, { color: '#7C3AED' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <TouchableOpacity style={styles.disabledButton} disabled>
                <ActivityIndicator size="small" color={theme.colors.white} />
                <Text style={styles.primaryButtonText}>Processing...</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#8B5CF6' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as const,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as const,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as const,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as const,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  typeSection: {
    marginBottom: theme.spacing.lg,
  },
  typeTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  typeGrid: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  typeCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  typeNumber: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.xs,
    marginBottom: 2,
  },
  typeLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  insightCard: {
    padding: theme.spacing.lg,
    backgroundColor: '#8B5CF6' + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: '#8B5CF6' + '20',
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  insightTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: '#8B5CF6',
  },
  insightText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
    lineHeight: 18,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  vehicleSection: {
    marginBottom: theme.spacing.lg,
  },
  vehicleTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  vehicleGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  vehicleCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  vehicleNumber: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.xs,
    marginBottom: 2,
  },
  vehicleLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  downloadSection: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  downloadTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  downloadButtons: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  downloadButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  downloadButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.white,
  },
  formatGrid: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  formatCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 2,
    borderColor: theme.colors.gray200,
  },
  formatIcon: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
  },
  formatName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: 4,
    textAlign: 'center',
  },
  formatDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  selectedIndicator: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Field selection styles
  sectionSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.md,
  },
  fieldsContainer: {
    gap: theme.spacing.xs,
  },
  fieldItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  fieldItemEnabled: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary + '05',
  },
  fieldItemRequired: {
    borderColor: theme.colors.warning,
    backgroundColor: theme.colors.warning + '05',
  },
  fieldInfo: {
    flex: 1,
    marginRight: theme.spacing.md,
  },
  fieldLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as const,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  fieldLabelEnabled: {
    color: theme.colors.primary,
  },
  fieldLabelRequired: {
    color: theme.colors.warning,
  },
  requiredMark: {
    color: theme.colors.error,
    fontWeight: '600' as const,
  },
  fieldDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  fieldSummary: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
  },
  fieldSummaryText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
});
